// src/pages/FlacheGeothermie.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import app_sonden from "../assets/Erdsonden.png";
import app_kollektoren from "../assets/Erdkollektoren.png";
import PageIntro from "../components/PageIntro";

import {
  TrendingUp,
  VolumeX,
  Snowflake,
  Euro,
  BadgeCheck,
  Wrench,
  ArrowRight,
} from "lucide-react";

export default function FlacheGeothermie() {
  const [activeIndex, setActiveIndex] = useState(null);

  useEffect(() => {
    document.title =
      "Flache Geothermie: Erdkollektor oder Erdsonde? | Erdwärme-Experten";

    let meta = document.querySelector("meta[name='description']");
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      "Verständliche Übersicht zur flachen Geothermie: Erdkollektoren, Erdsonden, Funktionsweise, Vorteile, Voraussetzungen und typische Einsatzbereiche."
    );

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = "https://www.erdwaerme-experten.de/flache-geothermie";
  }, []);

  const benefits = [
    {
      title: "Stabile Effizienz über das Jahr",
      short: "Konstante Untergrundtemperaturen – unabhängig vom Wetter.",
      detail:
        "Erdgekoppelte Systeme nutzen das ganzjährig nahezu konstante Temperaturniveau des Untergrunds. Im Gegensatz zu Luft-Wärmepumpen sinkt die Quellentemperatur im Winter nicht deutlich ab. Dadurch bleiben Effizienz und Leistungszahl auch bei Frost stabil.",
      color: "from-sky-600 to-sky-500",
      icon: TrendingUp,
    },
    {
      title: "Sehr geringe Geräuschemissionen",
      short: "Kein Außengerät – nahezu geräuschlos im Betrieb.",
      detail:
        "Da keine Außenluft angesaugt wird, entfällt das Außengerät vollständig. Das reduziert die Schallemissionen erheblich und erleichtert den Einsatz in dicht bebauten oder schallkritischen Wohngebieten.",
      color: "from-emerald-600 to-emerald-500",
      icon: VolumeX,
    },
    {
      title: "Option zur passiven Kühlung",
      short: "Kühlen über das Erdreich – mit minimalem Strombedarf.",
      detail:
        "Im Sommer kann überschüssige Wärme aus dem Gebäude über den Solekreis in den kühleren Untergrund abgeführt werden. Dadurch lassen sich die Raumtemperaturen spürbar senken. Die Wärmepumpe bleibt dabei meist ausgeschaltet; betrieben wird überwiegend nur die Umwälzpumpe.",
      color: "from-cyan-600 to-cyan-500",
      icon: Snowflake,
    },
    {
      title: "Niedrige Betriebskosten",
      short: "Hohe Effizienz senkt dauerhaft die Energiekosten.",
      detail:
        "Durch die höheren Jahresarbeitszahlen erdgekoppelter Systeme wird weniger Strom pro erzeugter Kilowattstunde Wärme benötigt. Das führt langfristig zu geringeren und besser planbaren Betriebskosten.",
      color: "from-indigo-600 to-indigo-500",
      icon: Euro,
    },
    {
      title: "Attraktive Förderbedingungen",
      short: "Hohe Förderung dank sehr guter Effizienzwerte.",
      detail:
        "Erdwärmesysteme erreichen sehr gute Effizienzkennwerte und werden im Rahmen der Bundesförderung für effiziente Gebäude häufig attraktiv gefördert.",
      color: "from-rose-600 to-rose-500",
      icon: BadgeCheck,
    },
    {
      title: "Hohe Betriebssicherheit",
      short: "Weniger Verschleiß durch geschützte Wärmequelle.",
      detail:
        "Da alle wesentlichen Komponenten witterungsgeschützt arbeiten und keine Vereisung oder häufigen Lastwechsel auftreten, sind erdgekoppelte Wärmepumpen langlebig und wartungsarm.",
      color: "from-slate-600 to-slate-500",
      icon: Wrench,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-[#f6f3f2] pb-20 pt-14 sm:pt-20">
      {/* HERO */}
      <PageIntro
        eyebrow="Grundlagen & Systemvergleich"
        title="Flache Geothermie verständlich erklärt"
        description={
          <>
            Erfahren Sie, wie Erdkollektoren und Erdsonden funktionieren, welche
            Voraussetzungen wichtig sind und warum Erdwärme eine besonders
            effiziente Wärmequelle für Wärmepumpen sein kann.
          </>
        }
        badges={[
          "Erdkollektoren & Erdsonden",
          "Voraussetzungen verständlich erklärt",
          "Vorteile im Überblick",
        ]}
        actions={
          <>
            <Link
              to="/erdwaermepumpe-kosten"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-red-600 px-7 py-3.5 font-semibold text-white shadow-sm transition hover:bg-red-700"
            >
              Zum Kostenvergleich <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/report"
              className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-7 py-3.5 font-semibold text-slate-800 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
            >
              Grundstück individuell prüfen
            </Link>
          </>
        }
        className="mb-12"
      />

      {/* EINLEITUNG */}
      <div className="mx-4 max-w-6xl rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:mx-6 sm:p-8 lg:p-10 xl:mx-auto">
        <h2 className="mb-6 text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
          Was ist flache Geothermie?
        </h2>

        <p className="text-slate-700 mb-4 leading-relaxed">
          Flache Geothermie nutzt die im Untergrund gespeicherte Wärmeenergie
          bereits <b>wenige Meter unterhalb der Geländeoberkante</b>. Je nach
          System wird Wärme aus Tiefen von etwa <b>1 Meter bis typischerweise
          100 Meter</b> erschlossen.
        </p>

        <p className="text-slate-700 mb-4 leading-relaxed">
          Das Erdreich ist ab wenigen Metern Tiefe über das Jahr deutlich
          temperaturstabiler als die Außenluft. Diese Umweltwärme wird über
          <b> geschlossene Rohrsysteme</b> an eine Wärmepumpe übertragen und
          dort auf ein für Heizung und Warmwasser nutzbares Temperaturniveau
          angehoben.
        </p>

        <p className="text-slate-700 mb-4 leading-relaxed">
          Das Funktionsprinzip ähnelt einem Kühlschrank – nur umgekehrt:
          Die Wärmepumpe entzieht dem Erdreich Wärme und gibt sie im Gebäude ab.
          Bei geeigneter Auslegung kann das System im Sommer auch zur
          <b> passiven Kühlung</b> genutzt werden.
        </p>

        <p className="text-slate-700 mb-4 leading-relaxed">
          In der Praxis unterscheidet man vor allem zwei Systeme:
          <b> Erdkollektoren</b> und <b>Erdsonden</b>.
        </p>

        {/* ERDKOLLEKTOREN */}
        <h3 className="text-xl font-semibold text-red-700 mt-8 mb-3">
          Erdkollektoren
        </h3>

        <p className="text-slate-700 mb-3 leading-relaxed">
          Erdkollektoren sind <b>horizontal verlegte Rohrsysteme</b>, die meist
          in einer Tiefe von etwa <b>1,0 bis 1,5 m</b> unter Geländeoberkante
          installiert werden. Sie nutzen die oberflächennahe Erdwärme, die
          durch Sonneneinstrahlung, Niederschläge und Umweltwärme regeneriert
          wird.
        </p>

        <p className="text-slate-700 mb-3 leading-relaxed">
          Wegen der vergleichsweise geringen Tiefe ist die benötigte Fläche
          relativ groß. Erdkollektoren eignen sich daher besonders für
          Grundstücke mit ausreichend unversiegelter und nutzbarer Fläche.
        </p>

        <ul className="list-disc pl-6 text-slate-700 mb-6 space-y-2">
          <li>
            <b>Vorteile:</b> geringere Investitionskosten, einfache Installation,
            häufig ohne tiefe Bohrung
          </li>
          <li>
            <b>Nachteile:</b> höherer Flächenbedarf, stärkere Abhängigkeit von
            Bodenart, Platz und Randbedingungen
          </li>
        </ul>

        <img loading="lazy" decoding="async"
          src={app_kollektoren}
          alt="Erdkollektoren der flachen Geothermie – schematische Darstellung"
          className="mx-auto my-8 w-full max-w-3xl rounded-2xl border border-slate-200 shadow-sm"
        />

        {/* ERDSONDEN */}
        <h3 className="text-xl font-semibold text-red-700 mt-10 mb-3">
          Erdsonden
        </h3>

        <p className="text-slate-700 mb-3 leading-relaxed">
          Erdsonden werden <b>vertikal in den Untergrund gebohrt</b> und
          erreichen in der Praxis meist Tiefen von <b>40 bis 100 Metern</b>.
          Technisch sind auch größere Tiefen möglich, im Wohngebäudebereich
          sind jedoch etwa 100 m pro Sonde sehr häufig der relevante Bereich.
        </p>

        <p className="text-slate-700 mb-3 leading-relaxed">
          In diesen Tiefen herrscht ein nahezu konstantes Temperaturniveau über
          das gesamte Jahr. Dadurch arbeiten Erdsonden besonders effizient und
          liefern auch bei niedrigen Außentemperaturen zuverlässig Wärme.
        </p>

        <ul className="list-disc pl-6 text-slate-700 mb-6 space-y-2">
          <li>
            <b>Vorteile:</b> hohe Effizienz, geringer Flächenbedarf, sehr stabile
            Wärmequelle
          </li>
          <li>
            <b>Nachteile:</b> höhere Investitionskosten, Genehmigungen,
            Bohrung erforderlich
          </li>
        </ul>

        <img loading="lazy" decoding="async"
          src={app_sonden}
          alt="Erdsonden der flachen Geothermie – schematische Darstellung"
          className="mx-auto my-8 w-full max-w-3xl rounded-2xl border border-slate-200 shadow-sm"
        />

        {/* VORAUSSETZUNGEN */}
        <div className="mt-14">
          <h2 className="mb-6 text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
            Wovon hängt die Eignung eines Grundstücks ab?
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
              <h3 className="text-lg font-semibold text-slate-950 mb-3">
                Typische Prüfpunkte
              </h3>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>Bodenart und Wärmeleitfähigkeit des Untergrunds</li>
                <li>Grundwasserverhältnisse und Schutzgebiete</li>
                <li>Verfügbare Grundstücksfläche</li>
                <li>Bohrbarkeit und Zugänglichkeit</li>
                <li>Heizlast und Gebäudestandard</li>
              </ul>
            </div>

            <div className="rounded-2xl border border-blue-200 bg-blue-50 p-6">
              <h3 className="text-lg font-semibold text-slate-950 mb-3">
                Wichtig zu wissen
              </h3>
              <p className="text-slate-700 leading-relaxed">
                Nicht jedes Grundstück eignet sich gleich gut für Erdkollektoren
                oder Erdsonden. Ob Erdwärme sinnvoll ist, hängt immer von der
                Kombination aus Untergrund, Platz, Genehmigung und Wärmebedarf ab.
              </p>
            </div>
          </div>
        </div>

        {/* VORTEILE */}
        <div className="mt-16">
          <h2 className="mb-8 text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
            Vorteile flacher Geothermie gegenüber Luft-Wärmepumpen
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((b, i) => {
              const isOpen = activeIndex === i;

              return (
                <div
                  key={i}
                  className="relative h-72 perspective cursor-pointer"
                  onClick={() => setActiveIndex(isOpen ? null : i)}
                >
                  <div
                    className={`relative w-full h-full transition-transform duration-500 transform-style-preserve-3d ${
                      isOpen ? "rotate-y-180" : ""
                    }`}
                  >
                    <div
                      className={`absolute inset-0 rounded-2xl p-6 text-white bg-gradient-to-br ${b.color} backface-hidden flex flex-col`}
                    >
                      <div className="mb-4">
                        <b.icon className="w-10 h-10 text-white opacity-90 mb-4" />
                      </div>

                      <h3 className="text-xl font-semibold mb-2">{b.title}</h3>
                      <p className="text-sm opacity-90">{b.short}</p>

                      <span className="mt-auto text-xs opacity-70">
                        Klicken für Details
                      </span>
                    </div>

                    <div className="absolute inset-0 rounded-2xl bg-white p-6 shadow-md rotate-y-180 backface-hidden flex flex-col">
                      <h3 className="text-lg font-semibold text-slate-950 mb-3">
                        {b.title}
                      </h3>

                      <p className="text-sm text-slate-700 leading-relaxed">
                        {b.detail}
                      </p>

                      <button
                        className="mt-auto text-sm text-red-600 hover:text-red-700 font-medium"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveIndex(null);
                        }}
                      >
                        ← Zurück
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-14 rounded-2xl border border-red-100 bg-red-50 p-6 sm:p-8">
          <h3 className="text-xl font-semibold text-slate-950 mb-2">
            Sie möchten wissen, ob Erdwärme auf Ihrem Grundstück sinnvoll ist?
          </h3>
          <p className="text-slate-700 leading-relaxed">
            Allgemeine Informationen sind ein guter Einstieg. Ob Erdkollektor oder
            Erdsonde an Ihrem Standort wirklich sinnvoll ist, hängt aber von den
            lokalen Gegebenheiten ab.
          </p>
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <Link
              to="/report"
              title="Erdwärme auf dem Grundstück prüfen – GeoReport"
              className="inline-flex items-center justify-center bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-full shadow transition font-semibold"
            >
              Grundstück individuell prüfen
            </Link>
            <Link
              to="/erdwaermepumpe-kosten"
              className="inline-flex items-center justify-center bg-white border border-red-200 hover:border-red-300 text-red-700 px-6 py-3 rounded-full shadow-sm transition font-semibold"
            >
              Zum Kostenvergleich
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}